import React, { useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { Typography, useBreakpoint, Button } from '@americanexpress/aqx-components';
import DynAppBanner from 'src/universal/components/DynamicAppBanner';
import DynamicAppModal from 'src/universal/components/DynamicApp/DynamicAppModal';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import { DynamicAppModalContext } from 'src/universal/components/DynamicApp/redux';
import { getDynAppModalContent } from 'src/universal/components/DynamicApp/redux';
import removeTags from 'service_modules/utils/text-remove-tags';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import { getCombinedG169Data, tncKeys } from './redux';
import { getActiveFilter } from 'src/universal/redux/cardsFilter';
import {
  sendDTMClickEvent,
  mapUrlWithDtmLinkQuery
} from 'service_modules/models/dtm-tagging';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import styles from './__styles__/index.scss?modules';
import dynAppBannerStyles from './__styles__/dynAppBanner.scss?modules';
import { StrikethroughOffer } from 'src/universal/components/Strikethrough';

const getBackImgUrl = (breakPointVal, assetList) => {
  const valMap = { sm: 'mobile', md: 'tablet', lg: 'desktop' };
  const code = valMap[breakPointVal] || 'desktop';
  const asset = assetList.find(item => {
    return item.type === `background-${code}`;
  });
  return asset.url;
};

const OptimizationHero = ({
  staticG169Data,
  cardObj,
  tncInfo,
  activeFilter,
  dynAppModalContent
}) => {
  const ctaRefFromBanner = useRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleDynAppModalOpen = useCallback(() => setIsModalOpen(prev => !prev), []);

  const {
    header,
    title,
    description,
    assets,
    dtmEvents,
    learnMoreCtaText,
    backgroundColor,
    strikethroughOffer
  } =
    staticG169Data || {};
  const { current } = useBreakpoint();
  const backImgUrl = getBackImgUrl(current, assets);
  const cardArt = assets.find(asset => asset.type === 'card-art');
  const { learnMore, cardTitle } = cardObj;
  const { termLinks, tncLabels } = tncInfo || {};

  let dtmTmp = dtmEvents.find(dtm => dtm.key === 'learnMoreCta') || {};
  const learnMoreDtm = {
    ...dtmTmp,
    eventAsset: replaceStringWithVars({
      varsObject: { filterCat: activeFilter },
      string: dtmTmp.eventAsset
    }),
    tag: replaceStringWithVars({
      varsObject: { filterCat: activeFilter },
      string: dtmTmp.tag
    })
  };
  dtmTmp = dtmEvents.find(dtm => dtm.key === 'heroImpression') || {};
  const impressDtm = {
    ...dtmTmp,
    eventAsset: replaceStringWithVars({
      varsObject: { filterCat: activeFilter },
      string: dtmTmp.eventAsset
    })
  };

  return (
    <>
      <div className={styles.optimizationHero}>
        <LazyScrollMetricTracker scrollDtm={impressDtm} />
        <Typography className={styles.heroHeader} Component="h1">
          {header}
        </Typography>
        <div
          className={styles.backImgHolder}
          style={{ backgroundImage: `url(${backImgUrl}` }}
        >
          <div className={clsx(styles.contentPanel, styles[backgroundColor])}>
            <img className={styles.cardArt} src={cardArt?.url} alt={cardArt?.alt} />
            <div className={styles.contentBox}>
              {strikethroughOffer && (
                <StrikethroughOffer
                  className={styles.heroTitle}
                  Component="h2"
                  header={title}
                />
              )}
              {!strikethroughOffer && (
                <Typography className={styles.heroTitle} Component="h2">
                  {title}
                </Typography>
              )}
              <Typography className={styles.heroDesc}>{description}</Typography>
              <Button
                Component="a"
                className={styles.learnMoreCta}
                href={mapUrlWithDtmLinkQuery(learnMore?.url, learnMoreDtm)}
                onClick={() => {
                  sendDTMClickEvent(learnMoreDtm);
                }}
                aria-label={removeTags(`${learnMore?.title} about ${cardTitle}`)}
              >
                <p>{learnMoreCtaText || learnMore?.title}</p>
              </Button>
              <div className={styles.tncContainer}>
                {Array.isArray(termLinks) &&
                  termLinks.map((link, i) => {
                    const text = link?.data?.html || tncLabels[i];
                    const ariaLabel = removeTags(
                      `${removeTags(text)} for ${cardObj?.cardTitle}`
                    );
                    const dtmTmp =
                      dtmEvents.find(dtm => dtm.key === tncKeys[i]) || {};
                    const dtm = {
                      ...dtmTmp,
                      eventAsset: replaceStringWithVars({
                        varsObject: { filterCat: activeFilter },
                        string: dtmTmp.eventAsset
                      })
                    };

                    return (
                      <a
                        className={styles.tncLink}
                        key={i}
                        href={link?.data?.href}
                        onClick={() => {
                          sendDTMClickEvent(dtm);
                        }}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={ariaLabel}
                      >
                        <Sanitizer safelySetInnerHTML={text} />
                      </a>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <DynamicAppModalContext.Provider value={toggleDynAppModalOpen}>
        <DynAppBanner
          bannerDataInjected={staticG169Data.dynamicAppBanner}
          custStyles={dynAppBannerStyles}
          ctaRef={ctaRefFromBanner}
        />
        <DynamicAppModal
          modal={isModalOpen}
          toggle={toggleDynAppModalOpen}
          content={dynAppModalContent}
          buttonRef={ctaRefFromBanner}
        />
      </DynamicAppModalContext.Provider>
    </>
  );
};

const mapStateToProps = state => {
  return {
    ...getCombinedG169Data(state),
    activeFilter: getActiveFilter(state),
    dynAppModalContent: getDynAppModalContent(state)
  };
};

export default connect(mapStateToProps)(React.memo(OptimizationHero));
